import React, { useState, useEffect, useCallback } from "react";
import {
    Box, Typography, Card, CardContent,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Chip, CircularProgress, Alert, Tooltip, Skeleton,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, MenuItem, Select, FormControl, InputLabel,
    IconButton, Snackbar,
} from "@mui/material";
import FireTruckRoundedIcon    from "@mui/icons-material/FireTruckRounded";
import EditRoundedIcon         from "@mui/icons-material/EditRounded";
import PersonRoundedIcon       from "@mui/icons-material/PersonRounded";
import PhoneRoundedIcon        from "@mui/icons-material/PhoneRounded";
import ScaleRoundedIcon        from "@mui/icons-material/ScaleRounded";
import CheckCircleRoundedIcon  from "@mui/icons-material/CheckCircleRounded";
import { getData, putData, API_ENDPOINTS } from "../../services/apiService";

// ── Brand palette ─────────────────────────────────────────────────────────
const T = {
    teal:    "#007C84", tealDk: "#016268", tealLt: "#E0F4F5",
    orange:  "#FF7900", orangeLt: "#FFF0E0",
    emerald: "#059669", emeraldLt: "#D1FAE5",
    rose:    "#E11D48", roseLt:   "#FFE4E6",
    amber:   "#D97706", amberLt:  "#FEF3C7",
    indigo:  "#4F46E5", indigoLt: "#EEF2FF",
    bg:      "#F7FAFC", border: "#E2E8F0",
    text:    "#0F172A", muted:  "#64748B",
};

// ── Status config ─────────────────────────────────────────────────────────
const STATUS_CFG = {
    new:       { label: "New",       bg: T.indigoLt,  color: T.indigo,  dot: T.indigo  },
    in_review: { label: "In Review", bg: T.amberLt,   color: T.amber,   dot: T.amber   },
    quoted:    { label: "Quoted",    bg: T.tealLt,    color: T.teal,    dot: T.teal    },
    booked:    { label: "Booked",    bg: T.emeraldLt, color: T.emerald, dot: T.emerald },
    rejected:  { label: "Rejected",  bg: T.roseLt,    color: T.rose,    dot: T.rose    },
};
const STATUS_OPTIONS = Object.entries(STATUS_CFG).map(([value, cfg]) => ({ value, label: cfg.label }));

const StatusChip = ({ status }) => {
    const cfg = STATUS_CFG[status] || STATUS_CFG["new"];
    return (
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.6, px: 1.2, py: 0.3, borderRadius: "8px", bgcolor: cfg.bg, border: `1px solid ${cfg.color}22` }}>
            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: cfg.dot }} />
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: cfg.color, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{cfg.label}</Typography>
        </Box>
    );
};

const fmtDate = iso => iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

// ── Main component ────────────────────────────────────────────────────────
export default function HeavyCargoEnquiries() {
    const [enquiries,   setEnquiries]   = useState([]);
    const [total,       setTotal]       = useState(0);
    const [page,        setPage]        = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [loading,     setLoading]     = useState(true);
    const [error,       setError]       = useState(null);

    // Update dialog
    const [dialog,      setDialog]      = useState({ open: false, enquiry: null });
    const [newStatus,   setNewStatus]   = useState("");
    const [adminNotes,  setAdminNotes]  = useState("");
    const [saving,      setSaving]      = useState(false);
    const [snack,       setSnack]       = useState({ open: false, msg: "", sev: "success" });

    const toast = (msg, sev = "success") => setSnack({ open: true, msg, sev });

    const load = useCallback(async (p, rpp) => {
        setLoading(true); setError(null);
        try {
            const res = await getData(API_ENDPOINTS.heavyCargoEnquiries, { page: p + 1, limit: rpp });
            setEnquiries(res?.result?.enquiries || []);
            setTotal(res?.result?.total || 0);
        } catch (e) {
            setError(e?.response?.data?.message || "Failed to load enquiries");
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(page, rowsPerPage); }, [page, rowsPerPage, load]);

    const openDialog = (enquiry) => {
        setDialog({ open: true, enquiry });
        setNewStatus(enquiry.hct_status || "new");
        setAdminNotes(enquiry.hct_admin_notes || "");
    };
    const closeDialog = () => { setDialog({ open: false, enquiry: null }); setSaving(false); };

    const handleSave = async () => {
        if (!dialog.enquiry) return;
        setSaving(true);
        try {
            const url = `${API_ENDPOINTS.updateHeavyCargoEnquiry}/${dialog.enquiry.package_id}`;
            const res = await putData(url, { status: newStatus, admin_notes: adminNotes });
            if (res?.statusCode === 200) {
                setEnquiries(prev => prev.map(e =>
                    e.package_id === dialog.enquiry.package_id
                        ? { ...e, hct_status: newStatus, hct_admin_notes: adminNotes }
                        : e
                ));
                toast("Enquiry updated successfully.");
                closeDialog();
            } else {
                toast(res?.message || "Failed to update.", "error");
            }
        } catch (e) {
            toast(e?.response?.data?.message || "Failed to update.", "error");
        } finally { setSaving(false); }
    };

    const onDutyCount  = enquiries.filter(e => (e.hct_status || "new") === "new").length;
    const inReviewCount= enquiries.filter(e => e.hct_status === "in_review").length;
    const bookedCount  = enquiries.filter(e => e.hct_status === "booked").length;

    return (
        <Box sx={{ bgcolor: T.bg, minHeight: "100vh", pb: 4 }}>

            {/* ── Hero header ── */}
            <Box sx={{
                background: `linear-gradient(135deg, ${T.teal} 0%, ${T.tealDk} 100%)`,
                borderRadius: "20px", p: 3, mb: 3,
                display: "flex", justifyContent: "space-between", alignItems: "center",
                boxShadow: "0 4px 20px rgba(0,124,132,0.2)",
            }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box sx={{ width: 46, height: 46, borderRadius: "12px", bgcolor: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.25)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <FireTruckRoundedIcon sx={{ fontSize: 24, color: "#fff" }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#fff", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                            Heavy Cargo Enquiries
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.75)", mt: 0.3 }}>
                            Shipments that exceeded the online booking weight threshold
                        </Typography>
                    </Box>
                </Box>

                {/* Summary pills */}
                {!loading && (
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                        {[
                            { label: "Total",     value: total,         bg: "rgba(255,255,255,0.15)", color: "#fff" },
                            { label: "New",       value: onDutyCount,   bg: T.indigoLt,  color: T.indigo  },
                            { label: "In Review", value: inReviewCount, bg: T.amberLt,   color: T.amber   },
                            { label: "Booked",    value: bookedCount,   bg: T.emeraldLt, color: T.emerald },
                        ].map(s => (
                            <Box key={s.label} sx={{ display: "flex", alignItems: "center", gap: 0.8, bgcolor: s.bg, borderRadius: "10px", px: 1.5, py: 0.7, border: s.color === "#fff" ? "1px solid rgba(255,255,255,0.25)" : `1px solid ${s.color}22` }}>
                                <Typography sx={{ fontSize: 16, fontWeight: 800, color: s.color, lineHeight: 1, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{s.value}</Typography>
                                <Typography sx={{ fontSize: 11, fontWeight: 600, color: s.color, opacity: 0.85 }}>{s.label}</Typography>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }}>{error}</Alert>}

            {/* ── Table ── */}
            <Card sx={{ borderRadius: "16px", border: `1px solid ${T.border}`, boxShadow: "0 1px 4px rgba(15,23,42,0.06)", overflow: "hidden" }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: T.bg }}>
                                {["Date", "Customer", "Phone", "Trip ID", "Weight", "Cargo Type", "Status", "Admin Notes", ""].map(h => (
                                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11, color: T.muted, textTransform: "uppercase", letterSpacing: "0.4px", py: 1.5, borderBottom: `2px solid ${T.border}` }}>
                                        {h}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {loading ? (
                                Array.from({ length: 5 }).map((_, i) => (
                                    <TableRow key={i}>
                                        {Array.from({ length: 9 }).map((__, j) => <TableCell key={j}><Skeleton height={18} /></TableCell>)}
                                    </TableRow>
                                ))
                            ) : enquiries.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                                        <FireTruckRoundedIcon sx={{ fontSize: 40, color: T.border, mb: 1, display: "block", mx: "auto" }} />
                                        <Typography fontSize={13} color="text.secondary">No heavy cargo enquiries yet.</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                enquiries.map(row => {
                                    const customerName = row.Customer
                                        ? `${row.Customer.first_name ?? ""} ${row.Customer.last_name ?? ""}`.trim() || "—"
                                        : "—";
                                    return (
                                        <TableRow key={row.package_id} hover sx={{ "&:last-child td": { border: 0 } }}>
                                            <TableCell sx={{ fontSize: 12, color: T.muted, py: 1.5, whiteSpace: "nowrap" }}>{fmtDate(row.createdAt)}</TableCell>

                                            <TableCell sx={{ py: 1.5 }}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                                                    <Box sx={{ width: 24, height: 24, borderRadius: "7px", bgcolor: T.orangeLt, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                        <PersonRoundedIcon sx={{ fontSize: 14, color: T.orange }} />
                                                    </Box>
                                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.text }}>{customerName}</Typography>
                                                </Box>
                                            </TableCell>

                                            <TableCell sx={{ py: 1.5 }}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                    <PhoneRoundedIcon sx={{ fontSize: 13, color: T.muted }} />
                                                    <Typography sx={{ fontSize: 12, color: T.text }}>{row.Customer?.mobile_num || "—"}</Typography>
                                                </Box>
                                            </TableCell>

                                            <TableCell sx={{ fontSize: 12, color: T.muted, py: 1.5 }}>{row.enquiry_trip_id ?? "—"}</TableCell>

                                            <TableCell sx={{ py: 1.5 }}>
                                                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1.2, py: 0.3, bgcolor: T.amberLt, borderRadius: "8px", border: `1px solid ${T.amber}22` }}>
                                                    <ScaleRoundedIcon sx={{ fontSize: 12, color: T.amber }} />
                                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.amber }}>{Number(row.package_weight).toFixed(1)} kg</Typography>
                                                </Box>
                                            </TableCell>

                                            <TableCell sx={{ fontSize: 12, py: 1.5, textTransform: "capitalize", color: T.text }}>{row.cargo_type || "—"}</TableCell>

                                            <TableCell sx={{ py: 1.5 }}>
                                                <StatusChip status={row.hct_status || "new"} />
                                            </TableCell>

                                            <TableCell sx={{ py: 1.5, maxWidth: 200 }}>
                                                {row.hct_admin_notes ? (
                                                    <Tooltip title={row.hct_admin_notes} placement="top">
                                                        <Typography sx={{ fontSize: 12, color: T.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 180, cursor: "default" }}>
                                                            {row.hct_admin_notes}
                                                        </Typography>
                                                    </Tooltip>
                                                ) : (
                                                    <Typography sx={{ fontSize: 12, color: T.border, fontStyle: "italic" }}>No notes</Typography>
                                                )}
                                            </TableCell>

                                            <TableCell padding="none" sx={{ py: 1.5, pr: 1 }}>
                                                <Tooltip title="Update status & notes">
                                                    <IconButton size="small" onClick={() => openDialog(row)}
                                                        sx={{ color: T.teal, bgcolor: T.tealLt, borderRadius: "8px", "&:hover": { bgcolor: T.teal, color: "#fff" } }}>
                                                        <EditRoundedIcon sx={{ fontSize: 15 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[10, 20, 50]}
                    sx={{ borderTop: `1px solid ${T.border}`, fontSize: 13 }}
                />
            </Card>

            {/* ── Update dialog ── */}
            <Dialog open={dialog.open} onClose={closeDialog} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: "20px" } }}>
                <DialogTitle sx={{ fontWeight: 800, fontSize: 17, fontFamily: "'Plus Jakarta Sans', sans-serif", borderBottom: `1px solid ${T.border}`, pb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{ width: 32, height: 32, borderRadius: "10px", bgcolor: T.tealLt, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <EditRoundedIcon sx={{ fontSize: 17, color: T.teal }} />
                        </Box>
                        Update Enquiry Status
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ pt: 2.5 }}>
                    {dialog.enquiry && (
                        <Box sx={{ mb: 2.5, p: 2, bgcolor: T.bg, borderRadius: "12px", border: `1px solid ${T.border}` }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: "0.4px", mb: 1 }}>Enquiry Details</Typography>
                            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                                <Box>
                                    <Typography sx={{ fontSize: 11, color: T.muted }}>Customer</Typography>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.text }}>
                                        {dialog.enquiry.Customer ? `${dialog.enquiry.Customer.first_name} ${dialog.enquiry.Customer.last_name}`.trim() : "—"}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: 11, color: T.muted }}>Weight</Typography>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.amber }}>{Number(dialog.enquiry.package_weight).toFixed(1)} kg</Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: 11, color: T.muted }}>Date</Typography>
                                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.muted }}>{fmtDate(dialog.enquiry.createdAt)}</Typography>
                                </Box>
                                {dialog.enquiry.pickup_notes && (
                                    <Box sx={{ width: "100%" }}>
                                        <Typography sx={{ fontSize: 11, color: T.muted }}>Customer Notes</Typography>
                                        <Typography sx={{ fontSize: 13, color: T.text, fontStyle: "italic" }}>{dialog.enquiry.pickup_notes}</Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )}

                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel sx={{ fontSize: 13 }}>Status</InputLabel>
                        <Select
                            value={newStatus}
                            label="Status"
                            onChange={e => setNewStatus(e.target.value)}
                            sx={{ borderRadius: "12px", fontSize: 13 }}
                        >
                            {STATUS_OPTIONS.map(opt => (
                                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 13 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: STATUS_CFG[opt.value]?.dot }} />
                                        {opt.label}
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label="Admin Notes / Response"
                        multiline rows={4}
                        fullWidth
                        placeholder="Add details about the quote, availability, pricing, or any follow-up actions…"
                        value={adminNotes}
                        onChange={e => setAdminNotes(e.target.value)}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px", fontSize: 13 } }}
                        InputLabelProps={{ sx: { fontSize: 13 } }}
                    />
                </DialogContent>

                <DialogActions sx={{ borderTop: `1px solid ${T.border}`, px: 3, py: 2, gap: 1 }}>
                    <Button onClick={closeDialog} disabled={saving} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600 }}>Cancel</Button>
                    <Button variant="contained" disableElevation onClick={handleSave} disabled={saving}
                        startIcon={saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <CheckCircleRoundedIcon />}
                        sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, bgcolor: T.teal, "&:hover": { bgcolor: T.tealDk } }}>
                        {saving ? "Saving…" : "Save Update"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Snackbar ── */}
            <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}>
                <Alert severity={snack.sev} variant="filled" onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ borderRadius: "12px" }}>
                    {snack.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
}
